import { useEffect, useRef, useState } from "react";
import {
  checkRequestAmount,
  pickDisplayBound,
  type RequestAmountCheckInput,
  type RequestAmountEnvelopeDetails,
} from "../api/request-amount-check";

const DEBOUNCE_MS = 350;

export type EnvelopeVerdict = "ok" | "below_min" | "above_max" | "idle";

export type EnvelopeDisplayBound = {
  amount: number;
  currency: string;
};

export type EnvelopeRecheckResult = {
  verdict: EnvelopeVerdict;
  displayBound: EnvelopeDisplayBound | null;
  details: RequestAmountEnvelopeDetails | null;
};

export type UseRequestAmountEnvelopeState = {
  loading: boolean;
  verdict: EnvelopeVerdict;
  displayBound: EnvelopeDisplayBound | null;
  details: RequestAmountEnvelopeDetails | null;
  /** Re-run check immediately (e.g. on Continuar after prefetch miss). */
  recheck: () => Promise<EnvelopeRecheckResult>;
};

type HookInput = Omit<RequestAmountCheckInput, "amountAnchor"> & {
  amountAnchor?: "send" | "receive";
  enabled: boolean;
  fallbackCurrency: string;
};

/**
 * Debounced live probe of POST /widget/limits/request-amount-check.
 * Stale responses are ignored via a monotonic sequence counter.
 */
export const useRequestAmountEnvelope = (
  input: HookInput | null,
): UseRequestAmountEnvelopeState => {
  const [loading, setLoading] = useState(false);
  const [verdict, setVerdict] = useState<EnvelopeVerdict>("idle");
  const [displayBound, setDisplayBound] = useState<EnvelopeDisplayBound | null>(
    null,
  );
  const [details, setDetails] = useState<RequestAmountEnvelopeDetails | null>(
    null,
  );

  const seqRef = useRef(0);
  const inputRef = useRef(input);
  inputRef.current = input;

  const applyResult = (
    result: Awaited<ReturnType<typeof checkRequestAmount>>,
    fallbackCurrency: string,
  ): EnvelopeRecheckResult => {
    if (result.ok) {
      setDetails(null);
      setDisplayBound(null);
      setVerdict("ok");
      return { verdict: "ok", displayBound: null, details: null };
    }
    const side =
      result.code === "REQUEST_AMOUNT_BELOW_MIN" ? "min" : "max";
    const nextVerdict: EnvelopeVerdict =
      result.code === "REQUEST_AMOUNT_BELOW_MIN" ? "below_min" : "above_max";
    const bound = pickDisplayBound(result.details, side, fallbackCurrency);
    setDetails(result.details);
    setDisplayBound(bound);
    setVerdict(nextVerdict);
    return {
      verdict: nextVerdict,
      displayBound: bound,
      details: result.details,
    };
  };

  const idleResult = (): EnvelopeRecheckResult => ({
    verdict: "idle",
    displayBound: null,
    details: null,
  });

  const runCheck = async (
    payload: HookInput,
    seq: number,
  ): Promise<EnvelopeRecheckResult> => {
    try {
      const result = await checkRequestAmount({
        originCountryCode: payload.originCountryCode,
        destinationCountryCode: payload.destinationCountryCode,
        sourceCurrency: payload.sourceCurrency,
        destinationCurrency: payload.destinationCurrency,
        payoutMethod: payload.payoutMethod,
        amount: payload.amount,
        amountAnchor: payload.amountAnchor ?? "receive",
        ...(payload.payerCode ? { payerCode: payload.payerCode } : {}),
        ...(payload.stateCode ? { stateCode: payload.stateCode } : {}),
        ...(payload.corridorCode
          ? { corridorCode: payload.corridorCode }
          : {}),
      });
      if (seq !== seqRef.current) return idleResult();
      return applyResult(result, payload.fallbackCurrency);
    } catch {
      if (seq !== seqRef.current) return idleResult();
      // Unexpected error: treat as idle so Continuar can retry once.
      setDetails(null);
      setDisplayBound(null);
      setVerdict("idle");
      return idleResult();
    }
  };

  useEffect(() => {
    if (!input?.enabled) {
      seqRef.current += 1;
      setLoading(false);
      setVerdict("idle");
      setDisplayBound(null);
      setDetails(null);
      return;
    }

    const seq = ++seqRef.current;
    setLoading(true);
    setVerdict("idle");

    const timer = window.setTimeout(() => {
      void (async () => {
        await runCheck(input, seq);
        if (seq === seqRef.current) setLoading(false);
      })();
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
    // Intentionally key on a stable signature of inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    input?.enabled,
    input?.amount,
    input?.originCountryCode,
    input?.destinationCountryCode,
    input?.sourceCurrency,
    input?.destinationCurrency,
    input?.payoutMethod,
    input?.payerCode,
    input?.stateCode,
    input?.corridorCode,
    input?.amountAnchor,
    input?.fallbackCurrency,
  ]);

  const recheck = async (): Promise<EnvelopeRecheckResult> => {
    const current = inputRef.current;
    if (!current?.enabled) return idleResult();
    const seq = ++seqRef.current;
    setLoading(true);
    try {
      return await runCheck(current, seq);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  };

  return { loading, verdict, displayBound, details, recheck };
};
